/**
 * Delega raccolta per una cassa edile di un altro territorio (capitolato ottobre 2026, cap. 7).
 *
 * Registro separato: nessun incrocio con deleghe/archivio/posizioni. I dati anagrafici
 * sono sempre salvati nella riga; `lavoratoreId` è solo il collegamento alla scheda.
 * Dati puri, usabili anche dal client.
 */
import { z } from 'zod';
import { DateISOSchema } from '../validation.ts';

const str = z.string().nullable();

/** Giorno corrente in Italia (YYYY-MM-DD), anche se il server gira in UTC */
const oggiItalia = (): string => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });

const testoFacoltativo = z.string().trim().min(1).optional();

export const DelegaAltroTerritorioSchema = z.object({
  id: z.number(),
  lavoratoreId: z.number().nullable(),
  codiceFiscale: z.string(),
  cognome: z.string(),
  nome: z.string(),
  dataNascita: z.string(),
  territorioId: z.number(),
  dataDelega: z.string(),
  operatore: str,
  note: str,
  /** UTC, `YYYY-MM-DD HH:MM:SS` */
  createdAt: z.string(),
  updatedAt: str,
  statiniInviataAt: str,
  statiniCodiceInserimento: str,
});

export type DelegaAltroTerritorio = z.infer<typeof DelegaAltroTerritorioSchema>;

export const DelegaAltroTerritorioArricchitaSchema = DelegaAltroTerritorioSchema.extend({
  territorioNome: z.string(),
  /** "Cognome Nome (530)" se l'operatore è in operatori_cache */
  operatoreNome: str,
});

export type DelegaAltroTerritorioArricchita = z.infer<typeof DelegaAltroTerritorioArricchitaSchema>;

/**
 * Body di inserimento e modifica: o un lavoratore dell'anagrafica, o CF + cognome + nome.
 * `dataNascita` è facoltativa (si ricava dal CF), ma la riga salvata la deve avere.
 */
export const DelegaAltroTerritorioBodySchema = z.object({
  lavoratoreId: z.number().int().positive().optional(),
  codiceFiscale: testoFacoltativo,
  cognome: testoFacoltativo,
  nome: testoFacoltativo,
  dataNascita: DateISOSchema.optional(),
  territorioId: z.number().int().positive(),
  dataDelega: DateISOSchema.refine(d => d <= oggiItalia(), { message: 'La data delega non può essere futura' }),
  operatore: z.string().trim().min(1),
  note: z.string().nullable().optional(),
}).refine(
  b => b.lavoratoreId !== undefined || (!!b.codiceFiscale && !!b.cognome && !!b.nome),
  { message: 'Indicare un lavoratore dell\'anagrafica oppure codice fiscale, cognome e nome', path: ['lavoratoreId'] }
);

export type DelegaAltroTerritorioBody = z.infer<typeof DelegaAltroTerritorioBodySchema>;

/** Riga da scrivere: nominativo già risolto */
export type DelegaAltroTerritorioInsert = Pick<DelegaAltroTerritorio,
  'lavoratoreId' | 'codiceFiscale' | 'cognome' | 'nome' | 'dataNascita' | 'territorioId' | 'dataDelega' | 'operatore' | 'note'>;
